import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { usePopupAnalytics } from '@/composables/use-popup-analytics'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'
import { useNotificationsQueue } from '@/store/notification-que/notifications-queue'
import { isPWARuntimeCheck } from '@/utils/device'

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
export const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000

const AUTO_SHOW_DELAY_MS = 3000

export const useModalPlayOnAppStore = defineStore(
    'modal_play_on_app',
    () => {
        const showAgainAt = ref<number | null>(null)
        const hasSeenFirstSession = ref<boolean>(false)
        const autoShowTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
        const loggerStore = useLoggerStore()
        const popupAnalytics = usePopupAnalytics('play-on-app-modal')

        const blockedByFullScreen = ref(false)

        function setBlockedByFullScreen(value: boolean) {
            blockedByFullScreen.value = value
        }

        const canShowModal = computed(() => {
            if (showAgainAt.value === null) {
                return true
            }
            return Date.now() >= showAgainAt.value
        })

        function markDismissed(durationMs: number) {
            showAgainAt.value = Date.now() + durationMs
        }

        function init() {
            const appStore = useAppStore()
            const layoutStore = useLayoutStore()
            const queue = useNotificationsQueue()

            const isMobileBrowser =
                (appStore.platform === 'android' || appStore.platform === 'ios') &&
                !appStore.isAppEnv &&
                !isPWARuntimeCheck() &&
                appStore.isPortalEnv

            if (!isMobileBrowser) {
                return
            }

            if (!hasSeenFirstSession.value) {
                hasSeenFirstSession.value = true
                return
            }

            if (canShowModal.value) {
                autoShowTimeoutId.value = setTimeout(() => {
                    if (!blockedByFullScreen.value) {
                        queue.request({
                            name: 'play-on-app-modal',
                            weight: 2,
                            onOpen: () => {
                                loggerStore.logInterfaceEvents({
                                    eventName: 'interface_show',
                                    interface_target: 'modal-play-on-app',
                                })
                                popupAnalytics.shown({ trigger: 'page-load' })
                                layoutStore.openPlayOnAppModal()
                            },
                            onClose: (reason) => {
                                if (layoutStore.playOnAppModalOpened && reason !== 'removed') {
                                    popupAnalytics.closed(reason)
                                }
                                layoutStore.closePlayOnAppModal()
                            },
                        })
                    }
                }, AUTO_SHOW_DELAY_MS)
            }
        }

        function dispose() {
            const queue = useNotificationsQueue()

            if (autoShowTimeoutId.value) {
                clearTimeout(autoShowTimeoutId.value)
                autoShowTimeoutId.value = null
            }
            queue.remove('play-on-app-modal')
        }

        return {
            setBlockedByFullScreen,
            showAgainAt,
            hasSeenFirstSession,
            init,
            dispose,
            markDismissed,
        }
    },
    {
        persist: {
            key: 'modal_play_on_app',
            pick: ['showAgainAt', 'hasSeenFirstSession'],
        },
    },
)
